import classNames from 'classnames'
import React, { PropsWithChildren, useLayoutEffect } from 'react'

import { BackIcon } from '@icons/BackIcon'

import { Heading6 } from '@ui/Typography'

import { Loading } from '@global/Loading/Loading'
import IconButton from '@global/iconButton/IconButton'

import { ModalMobile } from './ModalMobile'
import { ModalTitle } from './common/components/ModalTitle'
import { cloneButtonMobile, isButtonValid } from './common/helpers/button'
import { CommonModalProps, CommonModalTitleProps } from './types'

import styles from './FullPageModalMobile.module.scss'

interface FullPageModalProps extends PropsWithChildren<CommonModalTitleProps & CommonModalProps> {
  renderFooter?(props: FullPageModalProps): React.ReactNode[]
  goBack?(): void
}

const useEffectModalOpen = () => {
  useLayoutEffect(() => {
    document.documentElement.classList.add('full-page-modal-open')

    return () => {
      document.documentElement.classList.remove('full-page-modal-open')
    }
  }, [])
}

export const FullPageModalMobile: React.FC<FullPageModalProps> = (props) => {
  const { isLoading = false, onClose } = props

  useEffectModalOpen()

  return (
    <ModalMobile onClose={onClose}>
      <Loading isLoading={isLoading} showLoadingIcon>
        <FullPageBodyModalMobile {...props} />
      </Loading>
      <div className={styles.background} />
    </ModalMobile>
  )
}

export const FullPageBodyModalMobile: React.FC<FullPageModalProps> = (props) => {
  const { title, children, renderFooter, onClose } = props

  return (
    <div className={styles.wrapper}>
      <div className={styles.appHeader} />
      {title && <Header {...props} />}
      <section className={styles.body}>
        <div className={styles.content}>
          <div className={styles.children}>{children}</div>
          <FullPageModalMobileFooter {...props} buttons={renderFooter?.({ ...props, onClose })} />
        </div>
      </section>
    </div>
  )
}

const Header: React.FC<FullPageModalProps> = (props) => {
  const { title, goBack } = props

  return (
    <header className={styles.header}>
      <div className={styles.title}>
        <div className={styles.buttonWrapper}>
          <Heading6 medium>
            <div className={classNames('is-flex', styles.title)}>
              {goBack && (
                <span className={styles.backButton}>
                  <IconButton onClick={goBack}>
                    <BackIcon />
                  </IconButton>
                </span>
              )}
              <span>
                <ModalTitle title={title} />
              </span>
            </div>
          </Heading6>
        </div>
      </div>
    </header>
  )
}

interface FullPageModalFooterProps {
  buttons?: React.ReactNode[]
}

const FullPageModalMobileFooter: React.FC<FullPageModalFooterProps> = (props) => {
  const { buttons = [] } = props

  if (buttons?.length > 0) {
    return (
      <footer className={classNames('modal-card-foot', styles.footer)}>
        {buttons.map((button, index) => {
          return isButtonValid(button) ? (
            <span
              className={classNames(styles.footerButton, {
                [styles.footerButtons]: buttons?.length === 3,
              })}
              key={index}
            >
              {cloneButtonMobile(button)}
            </span>
          ) : (
            <span key={index}>{button}</span>
          )
        })}
      </footer>
    )
  }

  return null
}
